import { useEffect, useMemo, useRef, useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { EnvelopeSimple, PaperPlaneTilt, Spinner, Warning, Ruler } from "@phosphor-icons/react";

// Live preview of the tenant's branded email chrome + footer line + "send me a test" action.
export function EmailBrandingCard({ companyName, primaryColor, emailFooter, onEmailFooterChange, savedAt, logoUrl }) {
  const [html, setHtml] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [logoSize, setLogoSize] = useState(null);
  const timer = useRef(null);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (companyName) p.set("company_name", companyName);
    if (primaryColor) p.set("primary_color", primaryColor);
    if (emailFooter) p.set("email_footer", emailFooter);
    if (logoUrl) p.set("logo_url", logoUrl);
    if (urgent) p.set("urgent", "1");
    return p.toString();
  }, [companyName, primaryColor, emailFooter, logoUrl, urgent]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const [{ data }, size] = await Promise.all([
          API.get(`/tenant/branding/email-preview?${params}`, { responseType: "text" }),
          API.get(`/tenant/branding/email-logo-size`, { params: logoUrl ? { logo_url: logoUrl } : {} }).then((r) => r.data).catch(() => null),
        ]);
        setHtml(data);
        setLogoSize(size);
      } catch {
        setHtml("");
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [params, savedAt, logoUrl]);

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await API.post("/tenant/branding/email-test");
      toast.success(`Test email sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test email");
    } finally {
      setSending(false);
    }
  };

  const sizeNote = (() => {
    if (!logoSize) return null;
    if (logoSize.source === "fixlr") return { tone: "muted", text: `No logo uploaded yet — e-mails carry the Fixlr wordmark at ${logoSize.width} × ${logoSize.height} px. Upload your logo above and it will be fitted inside 300 × 110 px.` };
    const nat = logoSize.natural_width ? `${logoSize.natural_width} × ${logoSize.natural_height} px` : null;
    if (!logoSize.measured) return { tone: "warn", text: `Couldn't read the logo's size — it will be shown ${logoSize.box[0]} px wide with the height scaled to match.` };
    const small = logoSize.natural_width && logoSize.natural_width < logoSize.width * 2;
    return {
      tone: small ? "warn" : "ok",
      text: `Your logo appears at ${logoSize.width} × ${logoSize.height} px in every e-mail${nat ? ` (scaled from ${nat})` : ""}. Logos are fitted inside ${logoSize.box[0]} × ${logoSize.box[1]} px so they look the same in Gmail, Outlook and Apple Mail.${small ? " For a crisp result on retina screens upload at least " + logoSize.width * 2 + " px wide." : ""}`,
    };
  })();

  return (
    <div id="email-branding" className="mt-8 rounded-xl border border-border bg-card p-5 sm:p-6 scroll-mt-6" data-testid="email-branding-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <EnvelopeSimple size={18} className="text-primary" /> Email branding
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Every notification Fixlr sends on your behalf — to clients, suppliers and your team — uses your logo, company name and accent colour. Upload a logo above; the preview updates as you edit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
            <button type="button" onClick={() => setUrgent(false)} className={`px-3 py-1.5 ${!urgent ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`} data-testid="email-preview-standard">Standard</button>
            <button type="button" onClick={() => setUrgent(true)} className={`px-3 py-1.5 ${urgent ? "bg-red-600 text-white" : "bg-background hover:bg-muted"}`} data-testid="email-preview-urgent">Emergency</button>
          </div>
          <Button variant="outline" size="sm" onClick={sendTest} disabled={sending} data-testid="send-test-email-btn">
            {sending ? <Spinner size={14} className="animate-spin mr-1" /> : <PaperPlaneTilt size={14} className="mr-1" />} Send me a test
          </Button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-2">
          {sizeNote && (
            <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${sizeNote.tone === "ok" ? "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200" : sizeNote.tone === "warn" ? "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200" : "border-border bg-muted/40 text-muted-foreground"}`} data-testid="email-logo-size-note">
              <Ruler size={14} className="mt-0.5 shrink-0" /> <span>{sizeNote.text}</span>
            </div>
          )}
          <div className="rounded-lg border border-border bg-[#f1f5f9] overflow-hidden relative min-h-[420px]">
            {loading && <div className="absolute top-2 right-2 text-[10px] text-muted-foreground bg-white/80 px-2 py-0.5 rounded">Updating…</div>}
            {html ? (
              <iframe title="Email preview" srcDoc={html} sandbox="" className="w-full h-[520px] border-0" data-testid="email-preview-frame" />
            ) : (
              <div className="p-8 text-sm text-muted-foreground flex items-center gap-2"><Warning size={16} /> Preview unavailable</div>
            )}
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <Label htmlFor="email-footer" className="text-xs">Footer line (optional)</Label>
            <Input
              id="email-footer"
              value={emailFooter}
              onChange={(e) => onEmailFooterChange(e.target.value.slice(0, 300))}
              placeholder="Acme Facilities Ltd · Registered in England 01234567 · 0113 000 0000"
              className="mt-1"
              data-testid="email-footer-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">Shown under every email — company registration, address or support line. Saved with the button above.</p>
          </div>
          <ul className="text-xs text-muted-foreground space-y-1.5 list-disc pl-4">
            <li>Applies to all ~100 notification types: job updates, quotes, invoices, briefings, supplier onboarding, client portal emails.</li>
            <li>Emails sent by schedulers (daily briefings, reminders) pick up your branding from the recipient's company automatically.</li>
            <li>Clients with their own white-label branding keep their own logo on client-facing emails.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
